import {
  ForwardedRef,
  InputHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
} from 'react'
import { cn } from 'shared/styles'

type Props = {
  label: string
  errorMessage?: string
  extensible?: boolean
  isRequired?: boolean
  text?: string
  subText?: string
} & InputHTMLAttributes<HTMLTextAreaElement>

export const FormTextarea = forwardRef(
  (
    {
      label,
      isRequired,
      extensible,
      errorMessage,
      text,
      subText,
      ...props
    }: Props,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const handleInput = () => {
      if (textareaRef.current && extensible) {
        textareaRef.current.style.overflow = 'hidden'
        textareaRef.current.style.height = '98px'
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
      }
    }

    useEffect(() => {
      // A timeout is used to wait for the DOM to update so that scrollHeight reflects the actual content.
      // This is necessary when the textarea receives a large value and needs to resize itself based on the new content.
      const timer = setTimeout(() => {
        handleInput()
      }, 0)

      return () => clearTimeout(timer)
    }, [text])

    return (
      <div className='flex justify-between'>
        <label
          className={cn(
            'grow text-base font-medium text-gray-900',
            isRequired && 'required',
          )}
          htmlFor={props.name}
        >
          {label}
        </label>
        <div className='flex flex-col gap-y-1'>
          <textarea
            {...props}
            id={props.name}
            ref={(el) => {
              textareaRef.current = el
              if (typeof ref === 'function') {
                ref(el)
              } else if (ref) {
                ref.current = el
              }
            }}
            onInput={handleInput}
            className='base-input peer h-24 w-555 resize-none px-4 py-2.5'
          ></textarea>
          {subText && (
            <p className='hidden text-xs text-gray-500 peer-focus:block'>
              {subText}
            </p>
          )}
          {props['aria-invalid'] && errorMessage && (
            <p className='text-xs text-red-500'>{errorMessage}</p>
          )}
        </div>
      </div>
    )
  },
)
